import { startTransition, useEffect, useMemo, useState, type ReactNode } from "react"

import { Skeleton } from "@/components/ui/skeleton"
import {
  apiClient,
  type AiDisagreementRecord,
  type AiJobRecord,
  type AiKeywordRecord,
  type AiPerformanceSnapshot,
  type AiUrgentIncidentRecord,
  type OperatorOrganizationRecord,
  type PartnerDeliveryAttemptRecord,
  type PartnerDeliveryCallbackEventRecord,
  type PartnerDeliveryRecord,
  type PartnerIntegrationRecord,
  type PartnerDeliveryStatus,
} from "@/lib/api/client"
import {
  buildPartnerDeliveryChainSummaries,
  formatMaybeDate,
  INPUT_CLASS,
  matchesFilterText,
  safeJsonStringify,
  shortId,
  titleizeToken,
  type PartnerDeliveryChainSummary,
} from "@/features/operator/advanced/advanced-ops-utils"
import {
  OperatorEmptyState,
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  canAccessOpsMonitoringWorkspace,
  canManagePortalOrganizations,
  resolveOperatorSession,
  type OperatorResolvedSession,
} from "@/features/operator/shared/operator-access"

export interface OpsIntegrationsSearch {
  deliveryId?: string
  latestStatus?:
    | "all"
    | PartnerDeliveryStatus
  organizationId?: string
  partner?: string
  posture?: "all" | "dead_letter" | "delivered" | "in_flight" | "replayed" | "retry_queue"
  q?: string
}

interface OpsIntegrationsWorkspaceProps {
  onSearchChange: (next: Partial<OpsIntegrationsSearch>) => void
  search: OpsIntegrationsSearch
}

interface DeliveryTelemetry {
  attempts: PartnerDeliveryAttemptRecord[]
  callbackEvents: PartnerDeliveryCallbackEventRecord[]
  errorMessage?: string | null
  isLoading: boolean
}

interface OpsIntegrationsBundle {
  disagreements: AiDisagreementRecord[]
  integrations: PartnerIntegrationRecord[]
  jobs: AiJobRecord[]
  keywords: AiKeywordRecord[]
  organizations: OperatorOrganizationRecord[]
  performance: AiPerformanceSnapshot
  deliveries: PartnerDeliveryRecord[]
  chains: PartnerDeliveryChainSummary[]
  session: OperatorResolvedSession
  totalKeywords: number
  urgent: {
    incidents: AiUrgentIncidentRecord[]
    threshold?: number
  }
}

function canAccessOpsIntegrationsWorkspace(session: OperatorResolvedSession) {
  return Boolean(
    session.permissions.manageExports
      || session.permissions.viewAnalytics
      || canAccessOpsMonitoringWorkspace(session)
  )
}

async function loadOpsIntegrationsBundle(
  search: OpsIntegrationsSearch
): Promise<OpsIntegrationsBundle> {
  const session = await resolveOperatorSession()

  if (!canAccessOpsIntegrationsWorkspace(session)) {
    throw new Error("This session does not have access to integrations and AI oversight.")
  }

  const organizationId = canManagePortalOrganizations(session)
    ? search.organizationId
    : undefined

  const [
    integrationsResult,
    deliveriesResult,
    performanceResult,
    jobsResult,
    urgentResult,
    keywordsResult,
    disagreementsResult,
    organizationsResult,
  ] = await Promise.all([
    apiClient.getPartnerIntegrations({
      organizationId,
    }),
    apiClient.getPartnerDeliveries({
      limit: 200,
      organizationId,
    }),
    apiClient.getAiPerformance(),
    apiClient.getAiJobs(10),
    apiClient.getAiUrgentIncidents(),
    apiClient.getAiKeywords(),
    apiClient.getAiDisagreements(),
    canManagePortalOrganizations(session)
      ? apiClient.getOrganizations()
      : Promise.resolve(null),
  ])

  const deliveries = deliveriesResult.data ?? []

  return {
    chains: buildPartnerDeliveryChainSummaries(deliveries),
    deliveries,
    disagreements: disagreementsResult.data ?? [],
    integrations: integrationsResult.data ?? [],
    jobs: jobsResult.data ?? [],
    keywords: keywordsResult.data ?? [],
    organizations: organizationsResult?.data ?? [],
    performance: performanceResult.data,
    session,
    totalKeywords: keywordsResult.total_keywords ?? keywordsResult.data?.length ?? 0,
    urgent: {
      incidents: urgentResult.data ?? [],
      threshold: urgentResult.threshold,
    },
  }
}

export function OpsIntegrationsWorkspace({
  onSearchChange,
  search,
}: OpsIntegrationsWorkspaceProps) {
  const [bundle, setBundle] = useState<OpsIntegrationsBundle | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [telemetryByDeliveryId, setTelemetryByDeliveryId] = useState<
    Record<string, DeliveryTelemetry>
  >({})

  useEffect(() => {
    let isActive = true

    async function load() {
      startTransition(() => {
        setIsLoading(true)
        setErrorMessage(null)
      })

      try {
        const nextBundle = await loadOpsIntegrationsBundle(search)

        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(nextBundle)
          setIsLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(null)
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Failed to load the integrations workspace."
          )
          setIsLoading(false)
        })
      }
    }

    void load()

    return () => {
      isActive = false
    }
  }, [search.organizationId])

  const filteredChains = useMemo(() => {
    if (!bundle) {
      return []
    }

    return bundle.chains.filter((chain) => {
      if (search.partner && search.partner !== "all" && !chain.partnerKeys.includes(search.partner)) {
        return false
      }

      if (
        search.latestStatus
        && search.latestStatus !== "all"
        && chain.latestStatus !== search.latestStatus
      ) {
        return false
      }

      if (search.posture && search.posture !== "all" && chain.posture !== search.posture) {
        return false
      }

      return matchesFilterText(search.q, [
        chain.rootId,
        chain.partnerKeys,
        chain.regionKeys,
        chain.entitlementKeys,
        chain.purposeLabels,
        chain.governanceLabels,
        chain.externalDeliveryIds,
      ])
    })
  }, [bundle, search.latestStatus, search.partner, search.posture, search.q])

  const selectedChain = useMemo(() => {
    if (!bundle) {
      return null
    }

    if (search.deliveryId) {
      const directMatch = filteredChains.find((chain) =>
        chain.deliveries.some((delivery) => delivery.id === search.deliveryId)
      )

      if (directMatch) {
        return directMatch
      }
    }

    return filteredChains[0] ?? bundle.chains[0] ?? null
  }, [bundle, filteredChains, search.deliveryId])

  const selectedDelivery =
    selectedChain?.deliveries.find((delivery) => delivery.id === search.deliveryId)
    ?? selectedChain?.latestDelivery
    ?? null

  useEffect(() => {
    const deliveryId = selectedDelivery?.id

    if (!deliveryId || telemetryByDeliveryId[deliveryId]) {
      return
    }

    let isActive = true

    setTelemetryByDeliveryId((previous) => ({
      ...previous,
      [deliveryId]: {
        attempts: [],
        callbackEvents: [],
        isLoading: true,
      },
    }))

    void Promise.all([
      apiClient.getPartnerDeliveryAttempts(deliveryId),
      apiClient.getPartnerDeliveryCallbackEvents(deliveryId, 30),
    ])
      .then(([attemptsResponse, callbackResponse]) => {
        if (!isActive) {
          return
        }

        setTelemetryByDeliveryId((previous) => ({
          ...previous,
          [deliveryId]: {
            attempts: attemptsResponse.data ?? [],
            callbackEvents: callbackResponse.data ?? [],
            isLoading: false,
          },
        }))
      })
      .catch((error) => {
        if (!isActive) {
          return
        }

        setTelemetryByDeliveryId((previous) => ({
          ...previous,
          [deliveryId]: {
            attempts: [],
            callbackEvents: [],
            errorMessage:
              error instanceof Error
                ? error.message
                : "Failed to load callback or attempt telemetry.",
            isLoading: false,
          },
        }))
      })

    return () => {
      isActive = false
    }
  }, [selectedDelivery?.id, telemetryByDeliveryId])

  const selectedTelemetry = selectedDelivery
    ? telemetryByDeliveryId[selectedDelivery.id]
    : null
  const portalScoped = Boolean(bundle && canManagePortalOrganizations(bundle.session))
  const partnerOptions = useMemo(() => {
    if (!bundle) {
      return []
    }

    return Array.from(
      new Set(
        bundle.integrations
          .map((integration) => integration.partnerKey)
          .filter((partnerKey): partnerKey is string => Boolean(partnerKey))
      )
    ).sort()
  }, [bundle])

  return (
    <>
      {errorMessage ? (
        <OperatorStatusMessage tone="danger">{errorMessage}</OperatorStatusMessage>
      ) : null}

      {isLoading ? <OpsIntegrationsLoadingState /> : null}

      {!isLoading && bundle ? (
        <>
          <OperatorPanel
            title="Filters and scope"
            description="Filter integrations and delivery chains by organization, partner, status, and search text."
          >
            <div className="grid gap-4 xl:grid-cols-5">
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Organization scope</span>
                <select
                  aria-label="Organization scope"
                  className={INPUT_CLASS}
                  disabled={!portalScoped}
                  value={search.organizationId ?? ""}
                  onChange={(event) =>
                    onSearchChange({
                      organizationId: event.target.value || undefined,
                    })
                  }
                >
                  <option value="">All visible organizations</option>
                  {bundle.organizations.map((organization) => (
                    <option key={organization.id} value={organization.id}>
                      {organization.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Partner</span>
                <select
                  aria-label="Partner filter"
                  className={INPUT_CLASS}
                  value={search.partner ?? "all"}
                  onChange={(event) =>
                    onSearchChange({
                      partner:
                        event.target.value === "all" ? undefined : event.target.value,
                    })
                  }
                >
                  <option value="all">All partners</option>
                  {partnerOptions.map((partnerKey) => (
                    <option key={partnerKey} value={partnerKey}>
                      {titleizeToken(partnerKey)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Latest status</span>
                <select
                  aria-label="Latest status"
                  className={INPUT_CLASS}
                  value={search.latestStatus ?? "all"}
                  onChange={(event) =>
                    onSearchChange({
                      latestStatus:
                        event.target.value === "all"
                          ? undefined
                          : (event.target.value as OpsIntegrationsSearch["latestStatus"]),
                    })
                  }
                >
                  <option value="all">All statuses</option>
                  <option value="prepared">Prepared</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="queued">Queued</option>
                  <option value="processing">Processing</option>
                  <option value="submitted">Submitted</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="retryable">Retryable</option>
                  <option value="delivered">Delivered</option>
                  <option value="failed">Failed</option>
                  <option value="dead_letter">Dead letter</option>
                  <option value="skipped">Skipped</option>
                </select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Chain posture</span>
                <select
                  aria-label="Chain posture"
                  className={INPUT_CLASS}
                  value={search.posture ?? "all"}
                  onChange={(event) =>
                    onSearchChange({
                      posture:
                        event.target.value === "all"
                          ? undefined
                          : (event.target.value as OpsIntegrationsSearch["posture"]),
                    })
                  }
                >
                  <option value="all">All postures</option>
                  <option value="in_flight">In flight</option>
                  <option value="retry_queue">Retry queue</option>
                  <option value="dead_letter">Dead letter</option>
                  <option value="replayed">Replayed</option>
                  <option value="delivered">Delivered</option>
                </select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Search</span>
                <input
                  aria-label="Search chains"
                  className={INPUT_CLASS}
                  value={search.q ?? ""}
                  placeholder="Partner, export, region, delivery, governance"
                  onChange={(event) =>
                    onSearchChange({
                      q: event.target.value || undefined,
                    })
                  }
                />
              </label>
            </div>
          </OperatorPanel>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <OperatorMetricCard
              detail={`${bundle.integrations.filter((item) => item.status === "active").length} active profiles`}
              label="Integrations"
              value={bundle.integrations.length}
            />
            <OperatorMetricCard
              detail={`${bundle.deliveries.length} delivery records grouped into ${bundle.chains.length} chains`}
              label="Delivery chains"
              value={filteredChains.length}
            />
            <OperatorMetricCard
              detail={`Threshold ${bundle.urgent.threshold ?? 75} · ${bundle.disagreements.length} disagreements`}
              label="Urgent AI flags"
              value={bundle.urgent.incidents.length}
            />
            <OperatorMetricCard
              detail={`${bundle.performance.total_analyzed ?? 0} analyzed · ${bundle.totalKeywords} keywords`}
              label="AI oversight"
              value={bundle.jobs.length}
            />
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <OperatorPanel
              title="Partner integrations"
              description="Review rollout mode, callback readiness, and delivery handling for each partner."
            >
              {bundle.integrations.length ? (
                <div className="grid gap-3">
                  {bundle.integrations.map((integration) => (
                    <div
                      key={integration.id}
                      className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="text-sm font-semibold">
                            {integration.displayName || titleizeToken(integration.partnerKey)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {titleizeToken(integration.partnerKey)} ·{" "}
                            {titleizeToken(integration.status)}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                            {titleizeToken(integration.deliveryMode)}
                          </span>
                          <span className="rounded-full bg-background px-3 py-1">
                            {titleizeToken(integration.rolloutConfig?.mode)}
                          </span>
                        </div>
                      </div>
                      <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                        <div>
                          Callbacks{" "}
                          {integration.callbackConfig?.enabled === false
                            ? "disabled"
                            : "enabled"}
                        </div>
                        <div>
                          Max attempts {integration.deliveryPolicy?.maxAttempts ?? "default"}
                        </div>
                        <div>
                          Region {integration.regionKey || "default"}
                        </div>
                        <div>
                          DLQ {integration.deliveryPolicy?.deadLetterQueue || "not configured"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <OperatorEmptyState
                  title="No partner integrations in scope."
                  message="The current operator scope did not return any ecosystem partner profiles."
                />
              )}
            </OperatorPanel>

            <OperatorPanel
              title="Delivery chains"
              description="Review delivery history, replay activity, callback visibility, and current status."
            >
              {filteredChains.length ? (
                <div className="grid gap-3">
                  {filteredChains.map((chain) => (
                    <button
                      key={chain.id}
                      className={`rounded-3xl border px-4 py-4 text-left transition-colors ${
                        selectedChain?.id === chain.id
                          ? "border-primary/55 bg-primary/8"
                          : "border-border/70 bg-background/65 hover:border-primary/35"
                      }`}
                      type="button"
                      onClick={() =>
                        onSearchChange({
                          deliveryId: chain.latestDelivery.id,
                        })
                      }
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="text-sm font-semibold">
                            {titleizeToken(chain.partnerKeys[0])}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Root {shortId(chain.rootId)} · {chain.deliveries.length} delivery
                            {chain.deliveries.length === 1 ? "" : "ies"}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <span className="rounded-full bg-background px-3 py-1">
                            {titleizeToken(chain.posture)}
                          </span>
                          <span className="rounded-full bg-background px-3 py-1">
                            {titleizeToken(chain.latestStatus)}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <OperatorEmptyState
                  title="No delivery chains match the current filters."
                  message="Adjust the partner, posture, or text filters to inspect another chain."
                />
              )}
            </OperatorPanel>
          </div>

          <SelectedChainPanel
            chain={selectedChain}
            delivery={selectedDelivery}
            telemetry={selectedTelemetry}
            onSelectDelivery={(deliveryId) => onSearchChange({ deliveryId })}
          />

          <AiOversightPanel bundle={bundle} />
        </>
      ) : null}
    </>
  )
}

function SelectedChainPanel({
  chain,
  delivery,
  telemetry,
  onSelectDelivery,
}: {
  chain: PartnerDeliveryChainSummary | null
  delivery: PartnerDeliveryRecord | null
  onSelectDelivery: (deliveryId: string) => void
  telemetry?: DeliveryTelemetry | null
}) {
  if (!chain || !delivery) {
    return (
      <OperatorPanel
        title="Selected chain"
        description="Choose a chain to inspect callback events, attempt history, and replay lineage."
      >
        <OperatorEmptyState
          title="No chain selected."
          message="The right panel will show the mutable delivery state for the selected partner chain."
        />
      </OperatorPanel>
    )
  }

  const attempts = telemetry?.attempts ?? delivery.attempts ?? []
  const events = telemetry?.callbackEvents ?? []

  return (
    <OperatorPanel
      title="Selected chain"
      description="Callback inspection, latest attempt posture, and replay lineage remain attached to the selected delivery chain."
    >
      <div className="grid gap-5 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <div className="grid gap-3">
          <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold">
                  {delivery.integration?.displayName || titleizeToken(delivery.partnerKey)}
                </div>
                <div className="text-sm text-muted-foreground">
                  Delivery {shortId(delivery.id)} · key {shortId(delivery.deliveryKey, 12)}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                  {titleizeToken(delivery.status)}
                </span>
                <span className="rounded-full bg-background px-3 py-1">
                  {titleizeToken(delivery.liveMode)}
                </span>
              </div>
            </div>
            <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
              <div>Latest callback {formatMaybeDate(delivery.execution?.lastCallbackAt)}</div>
              <div>External ID {delivery.execution?.externalDeliveryId || "Not assigned"}</div>
            </div>
          </div>

          <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
            <div className="text-sm font-semibold">Chain members</div>
            <div className="mt-3 grid gap-2">
              {chain.deliveries.map((item) => (
                <button
                  key={item.id}
                  className={`rounded-2xl border px-3 py-3 text-left ${
                    item.id === delivery.id
                      ? "border-primary/55 bg-primary/8"
                      : "border-border/70 bg-background/70"
                  }`}
                  type="button"
                  onClick={() => onSelectDelivery(item.id)}
                >
                  <div className="text-sm font-medium">
                    {titleizeToken(item.status)} · {shortId(item.id)}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid gap-3">
          <TelemetryListPanel
            description="Attempt history remains newest-first and exposes response codes, retry posture, and request/response snapshots."
            items={attempts}
            loading={Boolean(telemetry?.isLoading)}
            title="Attempt ledger"
            renderItem={(attempt) => <AttemptCard attempt={attempt} key={attempt.id} />}
          />
          <TelemetryListPanel
            description="Callback events preserve the request path, partner payload, processing outcome, and linked attempt ids."
            errorMessage={telemetry?.errorMessage}
            items={events}
            loading={Boolean(telemetry?.isLoading)}
            title="Callback events"
            renderItem={(event) => <CallbackCard event={event} key={event.id} />}
          />
        </div>
      </div>
    </OperatorPanel>
  )
}

function TelemetryListPanel<TItem>({
  description,
  errorMessage,
  items,
  loading,
  renderItem,
  title,
}: {
  description: string
  errorMessage?: string | null
  items: TItem[]
  loading?: boolean
  renderItem: (item: TItem) => ReactNode
  title: string
}) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="text-sm font-semibold">{title}</div>
      <p className="mt-1 text-sm leading-6 text-muted-foreground">{description}</p>
      {errorMessage ? (
        <OperatorStatusMessage tone="warning">{errorMessage}</OperatorStatusMessage>
      ) : null}
      <div className="mt-3 grid gap-3">
        {loading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`telemetry-loading-row-${index}`}
              className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4"
            >
              <Skeleton className="h-4 w-40" />
              <Skeleton className="mt-3 h-4 w-full" />
              <Skeleton className="mt-2 h-4 w-5/6" />
            </div>
          ))
        ) : items.length ? (
          items.map(renderItem)
        ) : (
          <OperatorEmptyState
            title={`No ${title.toLowerCase()} recorded.`}
            message="The current delivery chain has not produced telemetry for this panel yet."
          />
        )}
      </div>
    </div>
  )
}

function OpsIntegrationsLoadingState() {
  return (
    <>
      <OperatorPanel
        title="Filters and scope"
        description="Filter integrations and delivery chains by organization, partner, status, and search text."
      >
        <div className="grid gap-4 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={`ops-integrations-filter-${index}`}
              className="h-11 w-full rounded-2xl"
            />
          ))}
        </div>
      </OperatorPanel>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={`ops-integrations-metric-${index}`}
            className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
          >
            <Skeleton className="h-4 w-24" />
            <Skeleton className="mt-3 h-8 w-20" />
            <Skeleton className="mt-3 h-4 w-full" />
          </div>
        ))}
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <OperatorPanel
          title="Partner integrations"
          description="Review rollout mode, callback readiness, and delivery handling for each partner."
        >
          <div className="grid gap-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={`ops-integrations-partner-${index}`}
                className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="mt-2 h-4 w-28" />
                  </div>
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-20 rounded-full" />
                    <Skeleton className="h-6 w-20 rounded-full" />
                  </div>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {Array.from({ length: 4 }).map((_, detailIndex) => (
                    <Skeleton
                      key={`ops-integrations-partner-detail-${index}-${detailIndex}`}
                      className="h-4 w-full"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </OperatorPanel>

        <OperatorPanel
          title="Delivery chains"
          description="Review delivery history, replay activity, callback visibility, and current status."
        >
          <div className="grid gap-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={`ops-integrations-chain-${index}`}
                className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="mt-2 h-4 w-40" />
                  </div>
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-24 rounded-full" />
                    <Skeleton className="h-6 w-24 rounded-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </OperatorPanel>
      </div>
    </>
  )
}

function AttemptCard({
  attempt,
}: {
  attempt: PartnerDeliveryAttemptRecord
}) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium">
          Attempt {attempt.attemptNumber ?? 0} · {shortId(attempt.id)}
        </div>
        <div className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
          {titleizeToken(attempt.status)}
        </div>
      </div>
      <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
        <div>Attempted {formatMaybeDate(attempt.execution?.attemptedAt)}</div>
        <div>Response {attempt.execution?.responseCode ?? "n/a"}</div>
      </div>
      {attempt.requestPayload ? (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm font-medium">
            Request payload snapshot
          </summary>
          <pre className="mt-2 overflow-auto rounded-2xl bg-background p-3 text-xs">
            {safeJsonStringify(attempt.requestPayload)}
          </pre>
        </details>
      ) : null}
    </div>
  )
}

function CallbackCard({
  event,
}: {
  event: PartnerDeliveryCallbackEventRecord
}) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium">
          {titleizeToken(event.eventType)} · {shortId(event.id)}
        </div>
        <div className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
          {titleizeToken(event.callbackStatus || event.processingOutcome)}
        </div>
      </div>
      <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
        <div>Received {formatMaybeDate(event.receivedAt)}</div>
        <div>Path {event.requestPath || "Not captured"}</div>
      </div>
      {event.payload ? (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm font-medium">
            Callback payload snapshot
          </summary>
          <pre className="mt-2 overflow-auto rounded-2xl bg-background p-3 text-xs">
            {safeJsonStringify(event.payload)}
          </pre>
        </details>
      ) : null}
    </div>
  )
}

function AiOversightPanel({
  bundle,
}: {
  bundle: OpsIntegrationsBundle
}) {
  return (
    <OperatorPanel
      title="AI oversight"
      description="Review AI performance, urgent flags, disagreements, and recent processing activity."
    >
      <div className="grid gap-5 xl:grid-cols-2">
        <div className="grid gap-3">
          <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
            <div className="text-sm font-semibold">Performance snapshot</div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <div className="text-sm text-muted-foreground">Analyzed incidents</div>
                <div className="text-2xl font-semibold">
                  {bundle.performance.total_analyzed ?? 0}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Recent 30 days</div>
                <div className="text-2xl font-semibold">
                  {bundle.performance.recent_30_days ?? 0}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Type accuracy</div>
                <div className="text-lg font-semibold">
                  {bundle.performance.overall_metrics?.type_accuracy ?? "--"}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Severity accuracy</div>
                <div className="text-lg font-semibold">
                  {bundle.performance.overall_metrics?.severity_accuracy ?? "--"}
                </div>
              </div>
            </div>
          </div>

          <SmallListPanel
            title={`Urgent incidents · ${bundle.urgent.incidents.length}`}
            items={bundle.urgent.incidents.slice(0, 5)}
            renderItem={(incident) => (
              <div key={incident.id} className="rounded-2xl border border-border/70 px-3 py-3">
                <div className="text-sm font-medium">
                  Incident #{incident.id} · {titleizeToken(incident.type)}
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {incident.location?.name || "Unknown location"} · urgency{" "}
                  {incident.aiUrgencyScore ?? "n/a"}
                </div>
              </div>
            )}
            emptyTitle="No urgent incidents"
          />
        </div>

        <div className="grid gap-3">
          <SmallListPanel
            title={`Keywords · ${bundle.totalKeywords}`}
            items={bundle.keywords.slice(0, 8)}
            renderItem={(keyword) => (
              <div
                key={keyword.keyword}
                className="flex items-center justify-between rounded-2xl border border-border/70 px-3 py-3 text-sm"
              >
                <span>{keyword.keyword}</span>
                <span className="text-muted-foreground">{keyword.count}</span>
              </div>
            )}
            emptyTitle="No keywords"
          />
          <SmallListPanel
            title={`Disagreements · ${bundle.disagreements.length}`}
            items={bundle.disagreements.slice(0, 4)}
            renderItem={(item) => (
              <div key={item.id} className="rounded-2xl border border-border/70 px-3 py-3">
                <div className="text-sm font-medium">Incident #{item.id}</div>
                <div className="mt-1 text-sm text-muted-foreground">
                  User {titleizeToken(item.user_classification?.type)} /{" "}
                  {titleizeToken(item.user_classification?.severity)} vs AI{" "}
                  {titleizeToken(item.ai_classification?.type)} /{" "}
                  {titleizeToken(item.ai_classification?.severity)}
                </div>
              </div>
            )}
            emptyTitle="No disagreements"
          />
          <SmallListPanel
            title={`AI jobs · ${bundle.jobs.length}`}
            items={bundle.jobs.slice(0, 4)}
            renderItem={(job) => (
              <div key={job.id} className="rounded-2xl border border-border/70 px-3 py-3">
                <div className="text-sm font-medium">
                  {job.queueName || "AI queue"} · {titleizeToken(job.status)}
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  Incident #{job.incidentId ?? "n/a"} · stage {titleizeToken(job.stage)} ·
                  retries {job.retryCount ?? 0}/{job.maxRetries ?? 0}
                </div>
              </div>
            )}
            emptyTitle="No AI jobs"
          />
        </div>
      </div>
    </OperatorPanel>
  )
}

function SmallListPanel<TItem>({
  emptyTitle,
  items,
  renderItem,
  title,
}: {
  emptyTitle: string
  items: TItem[]
  renderItem: (item: TItem) => ReactNode
  title: string
}) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="text-sm font-semibold">{title}</div>
      <div className="mt-3 grid gap-3">
        {items.length ? (
          items.map(renderItem)
        ) : (
          <OperatorEmptyState
            title={emptyTitle}
            message="No records are available for this panel right now."
          />
        )}
      </div>
    </div>
  )
}
